import { Button } from './Button';

export interface FilterOption {
  label: string;
  value: string | boolean;
  checked: boolean;
}

export interface FilterGroup {
  title: string;
  key: string;
  options: FilterOption[];
  type: 'checkbox' | 'radio';
}

interface FilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (filters: Record<string, any>) => void;
  onReset: () => void;
  filterGroups: FilterGroup[];
  onChange: (groupKey: string, optionValue: string | boolean) => void;
}

export function FilterModal({
  isOpen,
  onClose,
  onApply,
  onReset,
  filterGroups,
  onChange
}: FilterModalProps) {
  if (!isOpen) return null;

  const handleApply = () => {
    const appliedFilters: Record<string, any> = {};

    filterGroups.forEach((group) => {
      if (group.type === 'checkbox') {
        // For checkboxes, collect all checked values
        const checkedValues = group.options
          .filter((opt) => opt.checked)
          .map((opt) => opt.value);

        if (checkedValues.length > 0) {
          appliedFilters[group.key] = checkedValues.length === 1 ? checkedValues[0] : checkedValues;
        }
      } else {
        // For radio, get the single checked value
        const checkedOption = group.options.find((opt) => opt.checked);
        if (checkedOption) {
          appliedFilters[group.key] = checkedOption.value;
        }
      }
    });

    onApply(appliedFilters);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Filter Options</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {filterGroups.map((group) => (
              <div key={group.key} className="space-y-3">
                <h4 className="text-sm font-semibold text-gray-700">{group.title}</h4>
                <div className="space-y-2">
                  {group.options.map((option) => (
                    <label
                      key={`${group.key}-${option.value}`}
                      className="flex items-center gap-3 cursor-pointer group"
                    >
                      <input
                        type={group.type}
                        name={group.key}
                        checked={option.checked}
                        onChange={() => onChange(group.key, option.value)}
                        className="w-4 h-4 text-[#047857] border-gray-300 rounded focus:ring-[#047857] focus:ring-offset-0 focus:ring-2 cursor-pointer"
                      />
                      <span className="text-sm text-gray-700 group-hover:text-gray-900">
                        {option.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <Button variant="ghost" onClick={onReset} size="md">
            Reset All
          </Button>
          <div className="flex gap-3">
            <Button variant="secondary" onClick={onClose} size="md">
              Cancel
            </Button>
            <Button variant="primary" onClick={handleApply} size="md">
              Apply Filters
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FilterModal;
